import { useMemo, useState } from "react"
import { Box, Checkbox, HStack, IconButton, Input, Popover, Portal, Stack, Table } from "@chakra-ui/react"
import { Button, PaginationControls, Text } from "@/components"
import { usePagination } from "@/hooks"
import { EsoDeal } from "@/types"

interface Props {
  deals: EsoDeal[]
  onDealClick?: (deal: EsoDeal) => void
  selectedDealId?: number
  onClearSelectedDeal?: () => void
}

type SortColumn = "stage" | "bucket" | "inceptionDate" | "dealManager" | "expirationDate" | "ltv"
type SortDirection = "asc" | "desc"

const ALL_STAGES = [
  "Accepted",
  "Check 1 Sent",
  "Check 2 Sent",
  "Client Passed",
  "Closed",
  "Contract Review",
  "ESO Passed",
  "Exercise Check Sent",
  "NDA Sent",
  "NDA Signed",
  "Offer Sent",
  "Open",
  "Ready for Offer",
  "Robo Reject",
]

const BUCKET_DEFINITIONS: Record<string, string[]> = {
  "Open": ["NDA Sent", "Open", "NDA Signed", "Ready for Offer"],
  "Rejected": ["Robo Reject", "ESO Passed", "Client Passed"],
  "Offer": ["Offer Sent"],
  "Closing": ["Contract Review", "Exercise Check Sent", "Check 1 Sent", "Check 2 Sent", "Accepted"],
}

const ALL_BUCKETS = ["Need Update", "Open", "Offer", "Closing", "Closed", "Rejected"]

const NEED_UPDATE_EXCLUDED_STAGES = new Set([
  "ESO Passed", "Closed", "Robo Reject", "Client Passed",
  "Exercise Check Sent", "Check 1 Sent", "Check 2 Sent",
])

const NEED_UPDATE_DAYS_THRESHOLD = 30

const BUCKET_ROW_COLORS: Record<string, { bg: string; borderColor: string }> = {
  "Need Update": { bg: "purple.50", borderColor: "purple.300" },
  "Open": { bg: "orange.50", borderColor: "orange.300" },
  "Rejected": { bg: "red.50", borderColor: "red.300" },
  "Offer": { bg: "yellow.50", borderColor: "yellow.300" },
  "Closing": { bg: "blue.50", borderColor: "blue.300" },
  "Closed": { bg: "green.50", borderColor: "green.300" },
}

const getBucketName = (stageName: string): string => {
  const lower = stageName.toLowerCase()
  for (const [bucketName, stages] of Object.entries(BUCKET_DEFINITIONS)) {
    if (stages.some((s) => s.toLowerCase() === lower)) return bucketName
  }
  if (lower === "closed") return "Closed"
  return stageName
}

const needsUpdate = (deal: EsoDeal): boolean => {
  const stageName = deal.dealStage?.name || ""
  if (NEED_UPDATE_EXCLUDED_STAGES.has(stageName)) return false
  const modifiedDate = new Date(deal.updatedAt || 0)
  const thresholdDate = new Date()
  thresholdDate.setDate(thresholdDate.getDate() - NEED_UPDATE_DAYS_THRESHOLD)
  return modifiedDate < thresholdDate
}

const getDealBucket = (deal: EsoDeal): string => {
  if (needsUpdate(deal)) return "Need Update"
  return getBucketName(deal.dealStage?.name || "")
}

const ITEMS_PER_PAGE = 50

export const CompanyDeals = ({ deals, onDealClick, selectedDealId, onClearSelectedDeal }: Props) => {
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc")
  const [selectedStages, setSelectedStages] = useState<Set<string>>(new Set(ALL_STAGES))
  const [selectedBuckets, setSelectedBuckets] = useState<Set<string>>(new Set(ALL_BUCKETS))
  const [searchText, setSearchText] = useState("")

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString()
  }

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortColumn(column)
      setSortDirection("asc")
    }
  }

  const handleStageToggle = (stage: string) => {
    const newSelected = new Set(selectedStages)
    if (newSelected.has(stage)) {
      newSelected.delete(stage)
    } else {
      newSelected.add(stage)
    }
    setSelectedStages(newSelected)
  }

  const handleSelectAll = () => {
    setSelectedStages(new Set(ALL_STAGES))
  }

  const handleClearAll = () => {
    setSelectedStages(new Set())
  }

  const handleBucketToggle = (bucket: string) => {
    const newSelected = new Set(selectedBuckets)
    if (newSelected.has(bucket)) {
      newSelected.delete(bucket)
    } else {
      newSelected.add(bucket)
    }
    setSelectedBuckets(newSelected)
  }

  const handleSelectAllBuckets = () => {
    setSelectedBuckets(new Set(ALL_BUCKETS))
  }

  const handleClearAllBuckets = () => {
    setSelectedBuckets(new Set())
  }

  const filteredDeals = useMemo(() => {
    const search = searchText.toLowerCase().trim()
    return deals.filter((deal) => {
      const stageName = deal.dealStage?.name || ""
      if (!selectedStages.has(stageName)) return false
      const bucket = getDealBucket(deal)
      if (!selectedBuckets.has(bucket)) return false
      if (search) {
        const fullName = `${deal.client.firstName} ${deal.client.lastName}`.toLowerCase()
        if (!fullName.includes(search)) return false
      }
      return true
    })
  }, [deals, selectedStages, selectedBuckets, searchText])

  const sortedDeals = useMemo(() => {
    if (!sortColumn) return filteredDeals

    return [...filteredDeals].sort((a, b) => {
      let comparison = 0

      switch (sortColumn) {
        case "stage":
          comparison = (a.dealStage?.name || "").localeCompare(b.dealStage?.name || "")
          break
        case "bucket":
          comparison = getDealBucket(a).localeCompare(getDealBucket(b))
          break
        case "inceptionDate":
          comparison = new Date(a.inceptionDate || 0).getTime() - new Date(b.inceptionDate || 0).getTime()
          break
        case "dealManager":
          comparison = (a.dealManager?.name || "").localeCompare(b.dealManager?.name || "")
          break
        case "expirationDate":
          const aDate = a.expirationDate ? new Date(a.expirationDate).getTime() : 0
          const bDate = b.expirationDate ? new Date(b.expirationDate).getTime() : 0
          comparison = aDate - bDate
          break
        case "ltv":
          comparison = (a.computedLtv ?? 0) - (b.computedLtv ?? 0)
          break
      }

      return sortDirection === "asc" ? comparison : -comparison
    })
  }, [filteredDeals, sortColumn, sortDirection])

  const { paginatedData: paginatedDeals, currentPage, totalPages, startIndex, endIndex, setPage, getPageNumbers } = usePagination(sortedDeals, ITEMS_PER_PAGE)

  const SortIndicator = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) return <span style={{ opacity: 0.3 }}> ↕</span>
    return <span> {sortDirection === "asc" ? "↑" : "↓"}</span>
  }

  const SortableHeader = ({ column, children }: { column: SortColumn; children: React.ReactNode }) => (
    <Table.ColumnHeader
      cursor="pointer"
      onClick={() => handleSort(column)}
      _hover={{ bg: "hover.bg" }}
    >
      {children}
      <SortIndicator column={column} />
    </Table.ColumnHeader>
  )

  const getRowHighlight = (deal: EsoDeal) => {
    const bucket = getDealBucket(deal)
    const colors = BUCKET_ROW_COLORS[bucket]
    if (colors) {
      return { bg: colors.bg, borderLeft: "3px solid", borderLeftColor: colors.borderColor }
    }
    return undefined
  }

  return (
    <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
      <HStack mb={4} justifyContent="space-between">
        <Text fontSize="lg" fontWeight="bold">
          Deals ({filteredDeals.length} of {deals.length})
        </Text>

        <HStack gap={2}>
          <Input
            size="sm"
            placeholder="Search by client name..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value)
              setPage(1)
            }}
            w="220px"
            variant="outline"
            fontFamily="body"
            _dark={{ borderColor: "whiteAlpha.700" }}
          />
        <Popover.Root>
          <Popover.Trigger asChild>
            <Button size="sm" variant="outline">
              Filter by Bucket ({selectedBuckets.size}/{ALL_BUCKETS.length})
            </Button>
          </Popover.Trigger>
          <Portal>
            <Popover.Positioner>
              <Popover.Content w="250px">
                <Popover.Arrow />
                <Popover.Body>
                  <HStack mb={2} gap={2}>
                    <Button size="xs" variant="ghost" onClick={handleSelectAllBuckets}>
                      Select All
                    </Button>
                    <Button size="xs" variant="ghost" onClick={handleClearAllBuckets}>
                      Clear All
                    </Button>
                  </HStack>
                  <Stack maxH="300px" overflowY="auto" gap={1}>
                    {ALL_BUCKETS.map((bucket) => (
                      <Checkbox.Root
                        key={bucket}
                        checked={selectedBuckets.has(bucket)}
                        onCheckedChange={() => handleBucketToggle(bucket)}
                        size="sm"
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>{bucket}</Checkbox.Label>
                      </Checkbox.Root>
                    ))}
                  </Stack>
                </Popover.Body>
              </Popover.Content>
            </Popover.Positioner>
          </Portal>
        </Popover.Root>
        <Popover.Root>
          <Popover.Trigger asChild>
            <Button size="sm" variant="outline">
              Filter by Stage ({selectedStages.size}/{ALL_STAGES.length})
            </Button>
          </Popover.Trigger>
          <Portal>
            <Popover.Positioner>
              <Popover.Content w="250px">
                <Popover.Arrow />
                <Popover.Body>
                  <HStack mb={2} gap={2}>
                    <Button size="xs" variant="ghost" onClick={handleSelectAll}>
                      Select All
                    </Button>
                    <Button size="xs" variant="ghost" onClick={handleClearAll}>
                      Clear All
                    </Button>
                  </HStack>
                  <Stack maxH="300px" overflowY="auto" gap={1}>
                    {ALL_STAGES.map((stage) => (
                      <Checkbox.Root
                        key={stage}
                        checked={selectedStages.has(stage)}
                        onCheckedChange={() => handleStageToggle(stage)}
                        size="sm"
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>{stage}</Checkbox.Label>
                      </Checkbox.Root>
                    ))}
                  </Stack>
                </Popover.Body>
              </Popover.Content>
            </Popover.Positioner>
          </Portal>
        </Popover.Root>
        </HStack>
      </HStack>

      {sortedDeals.length === 0 ? (
        <Text color="text.muted">No deals found for the selected filters.</Text>
      ) : (
        <>
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Client</Table.ColumnHeader>
                <SortableHeader column="stage">Stage</SortableHeader>
                <SortableHeader column="bucket">Bucket</SortableHeader>
                <SortableHeader column="inceptionDate">Inception Date</SortableHeader>
                <SortableHeader column="expirationDate">Expiration Date</SortableHeader>
                <Table.ColumnHeader>Modified Date</Table.ColumnHeader>
                <SortableHeader column="ltv">LTV</SortableHeader>
                <SortableHeader column="dealManager">Deal Manager</SortableHeader>
                {selectedDealId && <Table.ColumnHeader w="50px"></Table.ColumnHeader>}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {paginatedDeals.map((deal) => {
                const isSelected = selectedDealId === deal.id
                return (
                  <Table.Row
                    key={deal.id}
                    {...(isSelected ? { bg: "row.blue", borderLeft: "3px solid", borderLeftColor: "blue.400" } : getRowHighlight(deal))}
                    cursor={onDealClick ? "pointer" : undefined}
                    _hover={onDealClick ? { opacity: 0.8 } : undefined}
                    onClick={() => onDealClick?.(deal)}
                  >
                    <Table.Cell>
                      {deal.client.firstName} {deal.client.lastName}
                    </Table.Cell>
                    <Table.Cell>{deal.dealStage?.name || "N/A"}</Table.Cell>
                    <Table.Cell>{getDealBucket(deal)}</Table.Cell>
                    <Table.Cell>{formatDate(deal.inceptionDate)}</Table.Cell>
                    <Table.Cell>{formatDate(deal.expirationDate)}</Table.Cell>
                    <Table.Cell>{formatDate(deal.updatedAt)}</Table.Cell>
                    <Table.Cell>
                      {deal.computedLtv != null && deal.computedLtv > 0
                        ? `$${deal.computedLtv.toFixed(2)}`
                        : "—"}
                    </Table.Cell>
                    <Table.Cell>{deal.dealManager?.name || "N/A"}</Table.Cell>
                    {selectedDealId && (
                      <Table.Cell>
                        {isSelected && (
                          <IconButton
                            aria-label="Remove selected deal"
                            size="xs"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation()
                              onClearSelectedDeal?.()
                            }}
                          >
                            ✕
                          </IconButton>
                        )}
                      </Table.Cell>
                    )}
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table.Root>

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={sortedDeals.length}
            startIndex={startIndex}
            endIndex={endIndex}
            onPageChange={setPage}
            getPageNumbers={getPageNumbers}
          />
        </>
      )}
    </Box>
  )
}
